import type {
	PVSetupData,
	GetStatsResponseHistoryData,
} from 'src/types/requests/charts/get_stats_period';
import type { StatisticsData } from 'src/types/Chart';

// In Back-Up wiring mode:
// - Generated meter measures total house load
// - Grid meter measures grid supply
// => Actual solar = Generated - Grid
// => Battery = if inverter supplies more than house consumed (i.e. negative total)
// => Export = if grid_source < 0 (i.e. reverse feed into grid)

export const calculateBackUpSetup = (data: StatisticsData, pvSetupData: PVSetupData) => {
	const isGrid = data.device?.pv_usage === 'grid';
	const isSolar = data.device?.pv_usage === 'solar';

	(data?.resData?.sum || data?.resData?.history || []).forEach((obj) => {
		if (!obj) return;
		if (obj?.missing) return pvSetupData.history.push(obj);

		const tariff_id = obj?.tariff_id || '0';
		const dt = obj?.datetime;
		const item: GetStatsResponseHistoryData = getHistoryItem(obj, pvSetupData);

		const consumption = obj?.consumption || 0;
		const reversed = obj?.reversed || 0;
		const netEnergy = consumption - reversed;
		const cost = obj?.cost || 0;
		const costPerUnit = consumption ? (1000 / consumption) * cost : 0;

		if (isGrid) {
			item.grid_source += netEnergy;
			item.grid_cost += cost;
			// Exported energy from grid to outside
			if (item.grid_source < 0) {
				item.export_energy = -Math.abs(item.grid_source);
				item.grid_source = 0;
				if (cost) {
					item.export_energy_cost += (reversed / 1000) * costPerUnit;
				}
			}
		}

		if (isSolar) {
			// Solar device actually measures full house load
			item.generated_energy += netEnergy;
			// Total House is what inverter delivers
			item.total_house_energy += netEnergy;
		}

		// Real solar = House - Grid
		item.generated_energy = Math.max(0, item.generated_energy - item.grid_source);

		// Battery = if inverter supplies more than house consumed (charging)
		item.batt_invertor = Math.max(
			0,
			item.total_house_energy - item.generated_energy - item.grid_source,
		);
		if (cost && item.batt_invertor > 0) {
			item.batt_invertor_cost = (item.batt_invertor / 1000) * costPerUnit;
		}
		if (!pvSetupData.history.find((h) => h.datetime == dt && h.tariff_id == tariff_id)) {
			pvSetupData.history.push(item);
		}
	});
};

// In Parallel wiring mode:
// Total house energy = Grid source + Generated - Export
// - Inverter injects solar directly into the house circuit, alongside the grid
// - House consumption = Grid + Generated (no battery involvement tracked)
// - Reversed energy from grid is exported
export const calculateParallelSetup = (
	data: StatisticsData,
	pvSetupData: PVSetupData,
	balconyPV: boolean = false,
) => {
	const isGrid = data?.device?.pv_usage === 'grid';
	const isSolar = data?.device?.pv_usage === 'solar';

	(data?.resData?.sum || data?.resData?.history || []).forEach((obj) => {
		if (!obj) return;
		if (obj?.missing) return pvSetupData.history.push(obj);

		const tariff_id = obj?.tariff_id || '0';
		const dt = obj?.datetime;
		const item: GetStatsResponseHistoryData = getHistoryItem(obj, pvSetupData);

		const consumption = obj?.consumption || 0;
		const reversed = obj?.reversed || 0;
		const netEnergy = consumption - reversed;
		const cost = obj?.cost || 0;

		if (isGrid) {
			// Grid energy supplied to the house
			item.grid_source += consumption;
			item.grid_cost += cost;

			// Total house initially includes grid import
			item.total_house_energy += netEnergy;

			if (reversed > 0) {
				const costPerUnit = consumption ? (1000 / consumption) * cost : 0;
				// export_energy is negative for chart rendering (below x-axis)
				item.export_energy -= reversed;
				item.export_energy_cost += (reversed / 1000) * costPerUnit;
			}
		}

		if (isSolar) {
			// Use reversed flow for balcony PV, or direct consumption for standard inverter.
			const generatedEnergy = balconyPV ? Math.abs(reversed) : consumption;
			item.generated_energy += generatedEnergy;
			item.total_house_energy += generatedEnergy;
		}

		// Battery cannot be calculated reliably in parallel mode
		item.batt_invertor = 0; // Without UPS/House meter, battery flow is unknown

		if (!pvSetupData.history.find((h) => h.datetime === dt && h.tariff_id === tariff_id)) {
			pvSetupData.history.push(item);
		}
	});
};

// In Balcony PV mode:
// - Grid measures total consumption of the house
// - Solar devices inject reversed energy back to the grid
// - No battery logic involved
export const calculateBalconyPVSetup = (data: StatisticsData, pvSetupData: PVSetupData) => {
	return calculateParallelSetup(data, pvSetupData, true);
};

function getHistoryItem(
	obj: GetStatsResponseHistoryData,
	pvSetupData: PVSetupData,
): GetStatsResponseHistoryData {
	return (
		pvSetupData.history.find(
			(h) => h.datetime == obj.datetime && h?.tariff_id == (obj?.tariff_id || '0'),
		) || {
			...obj,
			grid_source: 0,
			grid_cost: 0,
			generated_energy: 0,
			total_house_energy: 0,
			export_energy: 0,
			export_energy_cost: 0,
			batt_invertor: 0,
			batt_invertor_cost: 0,
		}
	);
}
